import React, { useState, useEffect } from 'react';
import { Supplier, CreateSupplierFormData } from '../../types/supplier';
import {
  TruckIcon,
  UserIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';

interface SupplierFormProps {
  supplier?: Supplier | null;
  onSubmit: (data: CreateSupplierFormData) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface FormSection {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
}

const FORM_SECTIONS: Record<string, FormSection> = {
  basic: {
    title: 'Alapadatok',
    icon: TruckIcon,
    color: 'from-blue-500 to-indigo-600'
  },
  contact: {
    title: 'Kapcsolattartási adatok',
    icon: EnvelopeIcon,
    color: 'from-emerald-500 to-teal-600'
  },
  address: {
    title: 'Cím adatok',
    icon: MapPinIcon,
    color: 'from-orange-500 to-yellow-600'
  }
};

const SupplierForm: React.FC<SupplierFormProps> = ({
  supplier,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState<CreateSupplierFormData>({
    name: '',
    companyName: '',
    taxNumber: '',
    email: '',
    phone: '',
    address: {
      country: 'Magyarország',
      postalCode: '',
      city: '',
      street: '',
      houseNumber: ''
    },
    isActive: true,
    notes: ''
  });

  // Ha szerkesztünk, töltsük be az adatokat
  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name,
        companyName: supplier.companyName || '',
        taxNumber: supplier.taxNumber || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || {
          country: 'Magyarország',
          postalCode: '',
          city: '',
          street: '',
          houseNumber: ''
        },
        isActive: supplier.isActive,
        notes: supplier.notes || ''
      });
    }
  }, [supplier]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await onSubmit(formData);
      
      // Sikeres mentés toast
      if (supplier) {
        toast.success('Beszállító sikeresen frissítve!');
      } else {
        toast.success('Beszállító sikeresen létrehozva!');
      }
    } catch (error) {
      console.error('Error submitting supplier:', error);
      toast.error('Hiba történt a beszállító mentése során');
    }
  };

  const handleChange = (field: string, value: any) => {
    if (field.startsWith('address.')) {
      const addressField = field.replace('address.', '');
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title={supplier ? 'Beszállító szerkesztése' : 'Új beszállító'}
      size="4xl"
    >
      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        {/* Alapadatok szekció */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="flex flex-col sm:flex-row">
            <div className={`p-4 bg-gradient-to-br ${FORM_SECTIONS.basic.color} flex items-center justify-center sm:rounded-l-lg`}>
              <FORM_SECTIONS.basic.icon className="w-5 h-5 text-white" />
            </div>
            
            <div className="flex-1 p-4">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">{FORM_SECTIONS.basic.title}</h3>
              <div className="grid grid-cols-12 gap-4">
                {/* Beszállító neve */}
                <div className="col-span-12">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Beszállító neve *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Beszállító neve (lehet cég vagy magánszemély)"
                  />
                </div>

                {/* Cégnév */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cégnév
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => handleChange('companyName', e.target.value)}
                      className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 pl-10 pr-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder="Cégnév (opcionális)"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <BuildingOffice2Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    </div>
                  </div>
                </div>

                {/* Adószám */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Adószám
                  </label>
                  <input
                    type="text"
                    value={formData.taxNumber}
                    onChange={(e) => handleChange('taxNumber', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="12345678-1-23 (opcionális)"
                  />
                </div>

                {/* Státusz */}
                <div className="col-span-12">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Státusz</label>
                  <div className="mt-1.5">
                    <label className="inline-flex items-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 cursor-pointer transition-colors hover:border-primary-300 dark:hover:border-primary-400">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => handleChange('isActive', e.target.checked)}
                        className="h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Aktív beszállító</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Kapcsolattartási adatok szekció */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="flex flex-col sm:flex-row">
            <div className={`p-4 bg-gradient-to-br ${FORM_SECTIONS.contact.color} flex items-center justify-center sm:rounded-l-lg`}>
              <FORM_SECTIONS.contact.icon className="w-5 h-5 text-white" />
            </div>
            
            <div className="flex-1 p-4">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">{FORM_SECTIONS.contact.title}</h3>
              <div className="grid grid-cols-12 gap-4">
                {/* Email */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 pl-10 pr-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder="email@example.com (opcionális)"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    </div>
                  </div>
                </div>

                {/* Telefon */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Telefon
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 pl-10 pr-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder="+36 1 234 5678 (opcionális)"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <PhoneIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cím szekció */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="flex flex-col sm:flex-row">
            <div className={`p-4 bg-gradient-to-br ${FORM_SECTIONS.address.color} flex items-center justify-center sm:rounded-l-lg`}>
              <FORM_SECTIONS.address.icon className="w-5 h-5 text-white" />
            </div>
            
            <div className="flex-1 p-4">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">{FORM_SECTIONS.address.title}</h3>
              <div className="grid grid-cols-12 gap-4">
                {/* Ország */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ország
                  </label>
                  <input
                    type="text"
                    value={formData.address?.country || ''}
                    onChange={(e) => handleChange('address.country', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Magyarország (opcionális)"
                  />
                </div>

                {/* Irányítószám */}
                <div className="col-span-12 sm:col-span-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Irányítószám
                  </label>
                  <input
                    type="text"
                    value={formData.address?.postalCode || ''}
                    onChange={(e) => handleChange('address.postalCode', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="1234 (opcionális)"
                  />
                </div>

                {/* Házszám */}
                <div className="col-span-12 sm:col-span-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Házszám
                  </label>
                  <input
                    type="text"
                    value={formData.address?.houseNumber || ''}
                    onChange={(e) => handleChange('address.houseNumber', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="12 (opcionális)"
                  />
                </div>

                {/* Város */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Város
                  </label>
                  <input
                    type="text"
                    value={formData.address?.city || ''}
                    onChange={(e) => handleChange('address.city', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Budapest (opcionális)"
                  />
                </div>

                {/* Utca */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Utca
                  </label>
                  <input
                    type="text"
                    value={formData.address?.street || ''}
                    onChange={(e) => handleChange('address.street', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Fő utca (opcionális)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Megjegyzések */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
            Megjegyzések
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={3}
            className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
            placeholder="További megjegyzések..."
          />
        </div>

        {/* Gombok */}
        <div className="flex justify-end space-x-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 font-medium"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Mentés...' : supplier ? 'Frissítés' : 'Létrehozás'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SupplierForm; 